import React, { useEffect, useState, useMemo } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingBag, Menu, X, Search, User } from 'lucide-react';
import { LOGO } from '../mock';
import { useCart } from '../context/CartContext';
import { fetchProducts, fetchServices } from '../api';

const links = [
  { to: '/', label: 'Home' },
  { to: '/shop', label: 'Shop' },
  { to: '/services', label: 'Healing & Services' },
  { to: '/book', label: 'Book' },
  { to: '/gift-cards', label: 'Gift Cards' },
  { to: '/wellness', label: 'Wellness' },
  { to: '/programs', label: 'Programs' },
  { to: '/events', label: 'Events' },
  { to: '/community', label: 'Community' },
  { to: '/sacred-connections', label: 'Sacred Connections' },
  { to: '/discover', label: 'Discover' },
  { to: '/videos', label: 'Videos' },
  { to: '/reviews', label: 'Reviews' },
  { to: '/sacred-aiko', label: 'Sacred Aiko' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [q, setQ] = useState('');
  const [index, setIndex] = useState([]);
  const { count, setIsOpen } = useCart();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); setShowSearch(false); setQ(''); }, [location.pathname]);

  // Build a searchable index (products + services) once when search first opens.
  useEffect(() => {
    if (!showSearch || index.length) return;
    Promise.all([fetchProducts().catch(() => []), fetchServices().catch(() => [])]).then(([prods, svcs]) => {
      const p = (prods || []).map((x) => ({
        id: x.id, type: 'product', title: x.name, to: `/product/${x.id}`,
        cat: x.category || '', hay: [x.name, x.summary, x.tagline, x.description, x.category, x.form].filter(Boolean).join(' ').toLowerCase(),
      }));
      const s = (svcs || []).map((x) => ({
        id: x.id, type: 'service', title: x.name, to: `/book?service=${x.id}`,
        cat: 'service', hay: [x.name, x.description, x.summary, x.tagline, 'service healing'].filter(Boolean).join(' ').toLowerCase(),
      }));
      setIndex([...p, ...s]);
    });
  }, [showSearch, index.length]);

  const results = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return [];
    const words = term.split(/\s+/);
    let matched = index.filter((it) => words.every((w) => it.hay.includes(w)));
    if (matched.length === 0) matched = index.filter((it) => words.some((w) => it.hay.includes(w)));
    const seen = new Set();
    return matched
      .filter((it) => { const k = `${it.type}:${it.id}`; if (seen.has(k)) return false; seen.add(k); return true; })
      .slice(0, 8);
  }, [q, index]);

  const doSearch = (e) => {
    e.preventDefault();
    if (!q.trim()) return;
    navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
    setShowSearch(false);
    setQ('');
  };

  const goResult = (to) => { navigate(to); setShowSearch(false); setQ(''); };

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled ? 'bg-[#0a0708]/95 backdrop-blur-md border-b border-[#d4af37]/20 py-2' : 'bg-gradient-to-b from-black/60 to-transparent py-3'}`}>
      <nav className="max-w-7xl mx-auto px-5 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 group flex-shrink-0">
          <img src={LOGO} alt="Prestige Elements" className="h-11 w-11 md:h-13 md:w-13 rounded-full object-cover ring-1 ring-[#d4af37]/40 transition-transform duration-500 group-hover:rotate-6" style={{ height: '3rem', width: '3rem' }} />
          <div className="leading-none hidden sm:block">
            <div className="font-cinzel text-base md:text-lg tracking-[0.16em] text-gold-gradient">PRESTIGE</div>
            <div className="font-cinzel text-[9px] md:text-[10px] tracking-[0.4em] text-pink-gradient">ELEMENTS</div>
          </div>
        </Link>

        <ul className="hidden xl:flex items-center gap-6 font-jost text-sm tracking-wide">
          {links.map((l) => (
            <li key={l.to}>
              <NavLink to={l.to} className={({ isActive }) => `nav-link uppercase text-[12px] whitespace-nowrap ${isActive ? 'active text-[#f0d582]' : 'text-[#f5ecd9]/80 hover:text-[#f0d582]'}`}>
                {l.label}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-4 flex-shrink-0">
          <button onClick={() => setShowSearch((s) => !s)} data-testid="nav-search-toggle" className="text-[#f5ecd9] hover:text-[#f0d582] transition-colors" aria-label="Search"><Search className="h-5 w-5" /></button>
          <Link to="/account" className="text-[#f5ecd9] hover:text-[#f0d582] transition-colors" aria-label="My Account"><User className="h-5 w-5" /></Link>
          <button onClick={() => setIsOpen(true)} className="relative text-[#f5ecd9] hover:text-[#f0d582] transition-colors" aria-label="Open cart">
            <ShoppingBag className="h-6 w-6" />
            {count > 0 && (<span className="absolute -top-2 -right-2 h-5 w-5 rounded-full bg-gradient-to-br from-[#d6197d] to-[#ff4da6] text-white text-[11px] flex items-center justify-center font-medium">{count}</span>)}
          </button>
          <button className="xl:hidden text-[#f5ecd9]" onClick={() => setMobileOpen((o) => !o)} aria-label="Menu">
            {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {showSearch && (
        <div className="max-w-7xl mx-auto px-5 pt-3" data-testid="nav-search-panel">
          <form onSubmit={doSearch} className="flex items-center gap-2 bg-white/5 border border-[#d4af37]/30 rounded-full px-4">
            <Search className="h-4 w-4 text-[#f0d582]" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} data-testid="nav-search-input" placeholder="Search crystals, herbs, services…" className="flex-1 bg-transparent py-3 text-sm text-[#f5ecd9] placeholder:text-[#f5ecd9]/40 focus:outline-none" />
            {q && <button type="button" onClick={() => setQ('')} data-testid="nav-search-clear" aria-label="Clear search" className="text-[#f5ecd9]/50 hover:text-[#f5ecd9]"><X className="h-4 w-4" /></button>}
            <button type="submit" data-testid="nav-search-submit" className="text-xs uppercase tracking-widest text-[#f0d582]">Go</button>
          </form>
          {q.trim() && (
            <div data-testid="nav-search-results" className="mt-2 bg-[#0f0b0d]/98 backdrop-blur-md border border-[#d4af37]/20 rounded-2xl overflow-hidden shadow-2xl">
              {results.length === 0 ? (
                <p data-testid="nav-search-empty" className="px-5 py-4 font-jost text-sm text-[#f5ecd9]/50">No results found for “{q.trim()}”.</p>
              ) : (
                <ul className="max-h-80 overflow-y-auto py-1">
                  {results.map((it) => (
                    <li key={`${it.type}:${it.id}`}>
                      <button onClick={() => goResult(it.to)} data-testid={`nav-search-result-${it.id}`} className="w-full text-left px-5 py-3 hover:bg-[#d4af37]/10 transition-colors flex items-center justify-between gap-3">
                        <span className="font-jost text-sm text-[#f5ecd9]">{it.title}</span>
                        <span className="text-[10px] uppercase tracking-widest text-[#f0d582]/70 shrink-0">{it.type === 'service' ? 'Service' : it.cat.replace(/-/g, ' ')}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
      )}

      {mobileOpen && (
        <div className="xl:hidden bg-[#0a0708]/98 backdrop-blur-md border-t border-[#d4af37]/15 mt-2 max-h-[80vh] overflow-y-auto">
          <ul className="flex flex-col px-6 py-2">
            {links.map((l) => (
              <li key={l.to} className="border-b border-white/5">
                <NavLink to={l.to} className={({ isActive }) => `block py-4 uppercase tracking-widest text-sm ${isActive ? 'text-[#f0d582]' : 'text-[#f5ecd9]/80'}`}>{l.label}</NavLink>
              </li>
            ))}
            <li className="border-b border-white/5"><NavLink to="/arcade" className="block py-4 uppercase tracking-widest text-sm text-[#f5ecd9]/80">Wellness Arcade</NavLink></li>
            <li className="border-b border-white/5"><NavLink to="/account" className="block py-4 uppercase tracking-widest text-sm text-[#f5ecd9]/80">My Account</NavLink></li>
            <li className="border-b border-white/5"><NavLink to="/ambassador" className="block py-4 uppercase tracking-widest text-sm text-[#f5ecd9]/80">Become an Ambassador</NavLink></li>
            <li className="py-4"><Link to="/book" className="btn-gold block text-center rounded-full py-3 text-sm font-semibold text-[#0a0708] bg-gradient-to-r from-[#d4af37] via-[#f0d582] to-[#d4af37]">Book A Healing</Link></li>
          </ul>
        </div>
      )}
    </header>
  );
};

export default Navbar;
